import { useState } from 'react'
import {
  WpciviModelCatalogBundle,
  WpciviExportBundle,
  wpciviBundleChecksumInput,
  wpciviMappingContext,
  CatalogRefreshRequest,
  type TCatalogClass,
  type TCatalogCategory,
  type TWpciviModelCatalogBundle,
  type TCatalogRefreshPreview,
  type TWpciviMappingRequest,
  type TWpciviMappingPreview,
} from '@schemas'
import {
  usePreviewCatalogRefresh,
  useApplyCatalogRefresh,
  usePreviewWpciviClassMappings,
  useExportWpciviClassMappings,
} from '@/api/hooks'
import { parseApiErrorMessage, parseApiErrorPayload, parseApiErrorStatus } from '@/lib/api-errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import RulesViewer from './RulesViewer'

type Draft = {
  selected: boolean
  categoryCode: string
  confirmed: boolean
  rules: string
  description: string
  flags: string
  subtypeKeys: string[]
  localSubtypes: string
}
const blank = (): Draft => ({
  selected: false,
  categoryCode: '',
  confirmed: false,
  rules: '{}',
  flags: '{}',
  description: '',
  subtypeKeys: [],
  localSubtypes: '',
})
const modelProvenance = (model: TCatalogClass) =>
  model.origin === 'wpcivi'
    ? `Civi ${model.sourceInstanceId}/${model.sourceKey}`
    : model.origin === 'app'
      ? 'App-managed'
      : model.origin === 'authored'
        ? 'Authored package'
        : 'Legacy'
const inputClass = 'w-full rounded-md border bg-background p-2 text-sm'
const MIB = 1024 * 1024
const sizeLimit = (bytes: number) =>
  bytes >= MIB ? `${Number((bytes / MIB).toFixed(1))} MiB` : `${Math.ceil(bytes / 1024)} KiB`
const message = (error: unknown) => {
  // A proxy can answer 413 without the API's JSON body, so the status alone decides.
  if (parseApiErrorStatus(error) === 413) {
    const limit = parseApiErrorPayload(error)?.limitBytes
    return `This review request is larger than the server accepts${
      typeof limit === 'number' ? ` (limit ${sizeLimit(limit)})` : ''
    }, so nothing was changed. Check that you uploaded the intended export file. If it is the right file, contact the system operator.`
  }
  return parseApiErrorMessage(error, 'Could not complete the review.')
}

export default function CiviCatalogTools({
  models,
  categories,
}: {
  models: TCatalogClass[]
  categories: TCatalogCategory[]
}) {
  const [mode, setMode] = useState<'catalog' | 'shows'>('catalog')
  const [bundle, setBundle] = useState<TWpciviModelCatalogBundle | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [preview, setPreview] = useState<TCatalogRefreshPreview | null>(null)
  const [mappingRequest, setMappingRequest] = useState<TWpciviMappingRequest | null>(null)
  const [mappingPreview, setMappingPreview] = useState<TWpciviMappingPreview | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const previewCatalog = usePreviewCatalogRefresh()
  const applyCatalog = useApplyCatalogRefresh()
  const previewMappings = usePreviewWpciviClassMappings()
  const exportMappings = useExportWpciviClassMappings()
  const busy =
    loading ||
    previewCatalog.isPending ||
    applyCatalog.isPending ||
    previewMappings.isPending ||
    exportMappings.isPending
  const templateNameCounts = new Map<string, number>()
  for (const model of models)
    templateNameCounts.set(model.name, (templateNameCounts.get(model.name) ?? 0) + 1)
  const baseChoices = models
    .filter(model => !model.retired)
    .flatMap<{
      model: TCatalogClass
      subtype: TCatalogClass['subtypes'][number] | null
      key: string
      label: string
    }>(model =>
      model.subtypes.length
        ? model.subtypes
            .filter(subtype => !subtype.retired)
            .map(subtype => ({
              model,
              subtype,
              key: `${model.id}/${subtype.id}`,
              label: `${model.categoryCode}: ${model.name} — ${subtype.name}`,
            }))
        : [
            {
              model,
              subtype: null,
              key: `${model.id}/`,
              label: `${model.categoryCode}: ${model.name}`,
            },
          ]
    )
  const labelCounts = new Map<string, number>()
  for (const choice of baseChoices)
    labelCounts.set(choice.label, (labelCounts.get(choice.label) ?? 0) + 1)
  const choices = baseChoices.map(choice => {
    if (labelCounts.get(choice.label) === 1) return choice
    return {
      ...choice,
      label: `${choice.label} (${modelProvenance(choice.model)}; model ${choice.model.id}${choice.subtype ? `; subtype ${choice.subtype.id}` : ''})`,
    }
  })
  const resetReview = () => {
    setPreview(null)
    setMappingPreview(null)
    setSuccess('')
    setError('')
  }
  const updateDraft = (key: string, updates: Partial<Draft>) => {
    resetReview()
    setDrafts(current => ({ ...current, [key]: { ...(current[key] ?? blank()), ...updates } }))
  }
  const catalogRequest = () =>
    CatalogRefreshRequest.parse({
      bundle,
      decisions: Object.entries(drafts)
        .filter(([, draft]) => draft.selected)
        .map(([sourceKey, draft]) => ({
          sourceKey,
          categoryCode: draft.categoryCode,
          ...(draft.confirmed
            ? {
                defaults: {
                  rules: JSON.parse(draft.rules),
                  flags: JSON.parse(draft.flags),
                  description: draft.description || null,
                },
              }
            : {}),
          subtypeKeys: draft.subtypeKeys,
          localSubtypes: draft.localSubtypes
            .split('\n')
            .map(name => name.trim())
            .filter(Boolean),
        })),
    })
  async function loadFile(file: File | undefined) {
    if (!file) return
    resetReview()
    setLoading(true)
    setBundle(null)
    setMappingRequest(null)
    setDrafts({})
    try {
      const raw = JSON.parse(await file.text())
      if (mode === 'catalog') {
        const source = WpciviModelCatalogBundle.parse(raw)
        const result = await previewCatalog.mutateAsync({ bundle: source, decisions: [] })
        setBundle(source)
        setPreview(result)
        setDrafts(
          Object.fromEntries(
            result.changes.map(change => {
              const current = models.find(model => model.id === change.modelId)
              return [
                change.sourceKey,
                {
                  ...blank(),
                  categoryCode: current?.categoryCode ?? '',
                  rules: JSON.stringify(current?.rules ?? {}, null, 2),
                  flags: JSON.stringify(current?.flags ?? {}, null, 2),
                  description: current?.description ?? '',
                },
              ]
            })
          )
        )
      } else {
        const source = WpciviExportBundle.parse(raw)
        const digest = await crypto.subtle.digest(
          'SHA-256',
          new TextEncoder().encode(wpciviBundleChecksumInput(source))
        )
        const checksum = Array.from(new Uint8Array(digest), byte =>
          byte.toString(16).padStart(2, '0')
        ).join('')
        setMappingRequest({ context: wpciviMappingContext(source, checksum), selections: [] })
      }
    } catch (error) {
      setError(message(error))
    } finally {
      setLoading(false)
    }
  }
  async function review() {
    setError('')
    setSuccess('')
    try {
      if (mode === 'catalog') setPreview(await previewCatalog.mutateAsync(catalogRequest()))
      else if (mappingRequest) setMappingPreview(await previewMappings.mutateAsync(mappingRequest))
    } catch (error) {
      resetReview()
      setError(message(error))
    }
  }
  async function commit() {
    setError('')
    try {
      if (mode === 'catalog' && preview) {
        const result = await applyCatalog.mutateAsync({
          ...catalogRequest(),
          reviewToken: preview.reviewToken,
        })
        setSuccess(
          `Applied ${result.appliedKeys.length} reviewed model changes. Existing show defaults are unchanged.`
        )
        setPreview(null)
        setBundle(null)
        setDrafts({})
      } else if (mappingRequest && mappingPreview) {
        const file = await exportMappings.mutateAsync({
          ...mappingRequest,
          reviewToken: mappingPreview.reviewToken,
        })
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' })
        )
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = 'wpcivi-class-mappings.json'
        anchor.click()
        URL.revokeObjectURL(url)
        setSuccess(
          'Mapping file downloaded. Use it with the same show bundle in the CLI dry run before applying.'
        )
        setMappingPreview(null)
      }
    } catch (error) {
      resetReview()
      setError(message(error))
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Civi file review</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={mode === 'catalog' ? 'default' : 'outline'}
            disabled={busy}
            onClick={() => {
              setMode('catalog')
              resetReview()
            }}
          >
            Review catalog refresh
          </Button>
          <Button
            variant={mode === 'shows' ? 'default' : 'outline'}
            disabled={busy}
            onClick={() => {
              setMode('shows')
              resetReview()
            }}
          >
            Prepare show mappings
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          {mode === 'catalog'
            ? 'Upload a verified Civi catalog export. Select the changes to accept, review their effects, then apply. Existing show defaults stay frozen.'
            : 'Upload a show bundle and select only the models and subtypes offered by each show. This prepares a mapping file; it does not import shows.'}
        </p>
        <label className="block space-y-2">
          {mode === 'catalog' ? 'Catalog JSON file' : 'Show bundle JSON file'}
          <Input
            key={mode}
            type="file"
            accept=".json,application/json"
            disabled={busy}
            onChange={event => void loadFile(event.target.files?.[0])}
          />
        </label>
        {error && (
          <p role="alert" className="whitespace-pre-wrap text-destructive">
            {error}
          </p>
        )}
        {success && <p role="status">{success}</p>}
        <fieldset disabled={busy} className="space-y-4">
          {mode === 'catalog' && bundle && (
            <>
              <p className="text-sm break-all">
                Source: {bundle.source.instanceId} · {bundle.source.url} · Publication{' '}
                {bundle.publication.publicationVersionKey}
              </p>
              <p className="text-sm break-all">Digest: {bundle.contentDigest}</p>
              {bundle.categories.map(source => {
                const draft = drafts[source.key] ?? blank()
                const model = models.find(
                  item =>
                    item.sourceInstanceId === bundle.source.instanceId &&
                    item.sourceKey === source.key
                )
                const selectable = ['selectable_class', 'parameterized_category'].includes(
                  source.kind
                )
                return (
                  <div key={source.key} className="rounded-md border p-3 space-y-3">
                    <label className="flex gap-2 items-center">
                      <input
                        type="checkbox"
                        checked={draft.selected}
                        disabled={!selectable}
                        onChange={event =>
                          updateDraft(source.key, { selected: event.target.checked })
                        }
                      />
                      {source.displayLabel}{' '}
                      <span className="text-muted-foreground text-sm">
                        {selectable
                          ? source.isActive
                            ? model
                              ? 'Update / keep'
                              : 'Add'
                            : 'Retired in source'
                          : 'Needs reconciliation'}
                      </span>
                    </label>
                    {draft.selected && (
                      <div className="space-y-3">
                        <label className="block">
                          App category
                          <select
                            aria-label={`App category for ${source.displayLabel}`}
                            className={inputClass}
                            value={draft.categoryCode}
                            onChange={event =>
                              updateDraft(source.key, { categoryCode: event.target.value })
                            }
                          >
                            <option value="">Select category</option>
                            {categories.map(category => (
                              <option key={category.code} value={category.code}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          Copy operational defaults from
                          <select
                            aria-label={`Copy defaults for ${source.displayLabel}`}
                            className={inputClass}
                            value=""
                            onChange={event => {
                              const template = models.find(item => item.id === event.target.value)
                              if (template)
                                updateDraft(source.key, {
                                  rules: JSON.stringify(template.rules, null, 2),
                                  flags: JSON.stringify(template.flags ?? {}, null, 2),
                                  description: template.description ?? '',
                                  confirmed: false,
                                })
                            }}
                          >
                            <option value="">Choose a starting model (optional)</option>
                            {models.map(item => (
                              <option key={item.id} value={item.id}>
                                {templateNameCounts.get(item.name) === 1
                                  ? item.name
                                  : `${item.categoryCode}: ${item.name} (${modelProvenance(item)}; model ${item.id})`}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          Operational rules (JSON)
                          <textarea
                            aria-label={`Operational rules for ${source.displayLabel}`}
                            className={inputClass}
                            rows={6}
                            value={draft.rules}
                            onChange={event =>
                              updateDraft(source.key, {
                                rules: event.target.value,
                                confirmed: false,
                              })
                            }
                          />
                        </label>
                        <label className="block">
                          Flags (JSON)
                          <textarea
                            className={inputClass}
                            value={draft.flags}
                            onChange={event =>
                              updateDraft(source.key, {
                                flags: event.target.value,
                                confirmed: false,
                              })
                            }
                          />
                        </label>
                        <label className="block">
                          Description
                          <Input
                            value={draft.description}
                            onChange={event =>
                              updateDraft(source.key, {
                                description: event.target.value,
                                confirmed: false,
                              })
                            }
                          />
                        </label>
                        <label className="flex gap-2">
                          <input
                            type="checkbox"
                            checked={draft.confirmed}
                            onChange={event =>
                              updateDraft(source.key, { confirmed: event.target.checked })
                            }
                          />
                          {model
                            ? 'Replace operational defaults with the values reviewed above'
                            : 'I reviewed these operational defaults, including label counts'}
                        </label>
                        {model && (
                          <p className="text-sm">
                            Leave replacement unchecked to preserve this model’s existing
                            operational defaults.
                          </p>
                        )}
                        {bundle.subtypes
                          .filter(subtype => subtype.parentKey === source.key)
                          .map(subtype => (
                            <label key={subtype.key} className="flex gap-2">
                              <input
                                type="checkbox"
                                checked={draft.subtypeKeys.includes(subtype.key)}
                                onChange={event =>
                                  updateDraft(source.key, {
                                    subtypeKeys: event.target.checked
                                      ? [...draft.subtypeKeys, subtype.key]
                                      : draft.subtypeKeys.filter(key => key !== subtype.key),
                                  })
                                }
                              />
                              Import/update subtype: {subtype.label} (existing retirement state
                              preserved)
                            </label>
                          ))}
                        <label className="block">
                          New app-managed subtypes (one per line)
                          <textarea
                            aria-label={`Local subtypes for ${source.displayLabel}`}
                            className={inputClass}
                            value={draft.localSubtypes}
                            onChange={event =>
                              updateDraft(source.key, { localSubtypes: event.target.value })
                            }
                          />
                        </label>
                        <details>
                          <summary>Source rule prose — reference only</summary>
                          {bundle.publication.blocks
                            .filter(block => block.categoryKeys.includes(source.key))
                            .map(block => (
                              <div key={block.sourceId}>
                                <h4>{block.title}</h4>
                                <pre className="whitespace-pre-wrap text-sm">{block.body}</pre>
                              </div>
                            ))}
                        </details>
                      </div>
                    )}
                  </div>
                )
              })}
              {preview && (
                <div className="space-y-3" aria-label="Catalog review summary">
                  {preview.warnings.map(warning => (
                    <p key={warning}>{warning}</p>
                  ))}
                  {preview.conflicts.map(conflict => (
                    <p key={conflict} role="alert">
                      {conflict}
                    </p>
                  ))}
                  {preview.changes.map(change => (
                    <details key={change.sourceKey}>
                      <summary>
                        {change.name}: {change.action} · {change.references} existing class
                        references ({change.subtypeReferences} with subtypes)
                      </summary>
                      <RulesViewer title="Current model" rules={change.before} />
                      <RulesViewer title="Proposed model" rules={change.proposed} />
                      {change.problems.map(problem => (
                        <p key={problem}>{problem}</p>
                      ))}
                    </details>
                  ))}
                </div>
              )}
            </>
          )}
          {mode === 'shows' && mappingRequest && (
            <>
              <p className="text-sm break-all">
                Show bundle checksum: {mappingRequest.context.bundleChecksum}
              </p>
              <p className="text-sm">
                Verify that this file belongs to the intended Civi source and target environment.
                Show files do not identify their source instance.
              </p>
              {mappingRequest.context.categories.map(
                (category, categoryIndex) =>
                  category.selected && (
                    <div key={categoryIndex} className="rounded-md border p-3 space-y-3">
                      <h3 className="font-medium">
                        {mappingRequest.context.shows.find(
                          show =>
                            JSON.stringify(show.sourceId) === JSON.stringify(category.showSourceId)
                        )?.name ?? category.showSourceId.id}
                        : {category.categoryName}
                      </h3>
                      <select
                        aria-label={`Add model for category ${categoryIndex + 1}`}
                        className={inputClass}
                        value=""
                        onChange={event => {
                          const choice = choices.find(item => item.key === event.target.value)
                          if (choice) {
                            resetReview()
                            setMappingRequest({
                              ...mappingRequest,
                              selections: [
                                ...mappingRequest.selections,
                                {
                                  categoryIndex,
                                  catalogClassId: choice.model.id,
                                  catalogSubtypeId: choice.subtype?.id ?? null,
                                },
                              ],
                            })
                          }
                        }}
                      >
                        <option value="">Select an exact model/subtype to add</option>
                        {choices.map(choice => (
                          <option key={choice.key} value={choice.key}>
                            {choice.label}
                          </option>
                        ))}
                      </select>
                      {mappingRequest.selections.map(
                        (selection, index) =>
                          selection.categoryIndex === categoryIndex && (
                            <div key={index} className="flex items-center gap-2">
                              <span>
                                {
                                  choices.find(
                                    choice =>
                                      choice.model.id === selection.catalogClassId &&
                                      (choice.subtype?.id ?? null) === selection.catalogSubtypeId
                                  )?.label
                                }
                              </span>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  resetReview()
                                  setMappingRequest({
                                    ...mappingRequest,
                                    selections: mappingRequest.selections.filter(
                                      (_, row) => row !== index
                                    ),
                                  })
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          )
                      )}
                      {!mappingRequest.selections.some(
                        selection => selection.categoryIndex === categoryIndex
                      ) && (
                        <p className="text-sm">
                          Unresolved: this category will produce an import issue and no classes.
                        </p>
                      )}
                    </div>
                  )
              )}
              {mappingPreview && (
                <div aria-label="Show mapping review summary">
                  {mappingPreview.issues.map(issue => (
                    <p key={issue} role="alert">
                      {issue}
                    </p>
                  ))}
                  {mappingPreview.selections.map((selection, index) => (
                    <p key={index}>
                      {selection.categoryCode}: {selection.modelName}
                      {selection.subtypeName ? ` — ${selection.subtypeName}` : ''} (model revision{' '}
                      {selection.modelRevision})
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={busy || (mode === 'catalog' ? !bundle : !mappingRequest)}
            onClick={() => void review()}
          >
            Preview selected changes
          </Button>
          <Button
            disabled={busy || !(mode === 'catalog' ? preview?.canApply : mappingPreview?.canExport)}
            onClick={() => void commit()}
          >
            {mode === 'catalog' ? 'Apply reviewed changes' : 'Download reviewed mapping file'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
